import React from 'react';

import { downloadUrl } from '@apis/common';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { RadioInputControl } from '@components/NextForms/RadioInputControl';
import { useDialogKey } from '@hooks/useDialogKey';
import { ExportCSVForm, ExportCSVHelper } from '@models/next/CSV';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { stringify } from 'query-string';

const strings = createStrings({
  ja: {
    exportMethod: 'エクスポート手段',
    title: (subject: string) => `${subject}をエクスポートする`,
    selected: (subject: string, count: number) => `選択済み: ${count}個の${subject}`,
    matched: (subject: string) => `検索内容と一致している${subject}`,
    all: (subject: string) => `すべての${subject}`,
  },
});

export interface ExportCSVDialgo2Props {
  open: boolean;
  onClose: () => void;
  baseUrl: string;
  subject: string;
  title?: string;
  selected: any[];
  query?: Record<string, any>;
  defaultQuery?: Record<string, any>;
}

const ExportCSVFormContent: React.FC<ExportCSVDialgo2Props> = (props) => {
  const { onClose, baseUrl, subject, title, selected, query, defaultQuery } = props;

  const classes = {
    ...useCommonStyles(),
  };

  const form = useForm(ExportCSVForm, {
    initialOutput: {
      method: selected.length > 0 ? 'selected' : 'matched',
    },
  });

  const { fields } = form.root;

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          const queries = ExportCSVHelper.mapFormToPaginatedQuery(
            values,
            selected,
            query ?? {},
            defaultQuery ?? {},
          );

          await downloadUrl({
            url: `${baseUrl}?${stringify(queries)}`,
            name: title ?? `${subject}.csv`,
            type: 'text/csv',
          });

          onClose();
        },
      })}
    >
      <ActionableDialogContent>
        <div className={clsx(classes.textGray700, classes.middleText)}>
          {fields.method.type.options.map((option) => (
            <RadioInputControl
              key={option}
              field={fields.method}
              value={option}
              disabled={option === 'selected' && selected.length === 0}
              labelChild={
                {
                  selected: strings.selected(subject, selected.length),
                  matched: strings.matched(subject),
                  all: strings.all(subject),
                }[option]
              }
            />
          ))}
        </div>
      </ActionableDialogContent>

      <ActionableDialogActions>
        <Button
          color="grey"
          size="small"
          text={formStrings.cancel}
          onClick={onClose}
          type="button"
        />

        <Button color="primary" size="small" text={formStrings.export(subject)} type="submit" />
      </ActionableDialogActions>
    </form>
  );
};

export const ExportCSVDialog2: React.FC<ExportCSVDialgo2Props> = (props) => {
  const { open, onClose, subject } = props;

  const dialogKey = useDialogKey(open);

  return (
    <ActionableDialog open={open} onClose={onClose}>
      <ActionableDialogTitle>{strings.title(subject)}</ActionableDialogTitle>

      <ExportCSVFormContent {...props} key={dialogKey} />
    </ActionableDialog>
  );
};
